import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, User, Briefcase } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldGroup, FieldSet, FieldLegend, FieldSeparator } from '@/components/ui/field';

import { type Contact } from '@/entities/contacts';
import { FormField } from '@/shared/ui';

import { useContactForm, ModalMode, getModalTitle } from '../model';
import { SubmitButton } from './submit-button';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (contact: Contact) => void;
  initialData?: Partial<Contact>;
  mode?: ModalMode;
}

export const ContactModal = ({ isOpen, onClose, onSuccess, initialData, mode = 'create' }: ContactModalProps) => {
  const { t } = useTranslation();
  const isViewMode = mode === 'view';
  const { form, onSubmit, isSaving, onCancel } = useContactForm({
    initialData,
    onSuccess,
    onCancel: onClose
  });

  const {
    register,
    formState: { errors }
  } = form;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b bg-slate-50/50">
          <DialogTitle className="text-sm font-bold text-slate-600 uppercase tracking-widest">
            {getModalTitle(mode, t)}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="p-6 max-h-[80vh] overflow-y-auto">
          <FieldGroup>
            {/* Identity Section */}
            <FieldSet>
              <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
                <User className="w-4 h-4" />
                {t('contacts.sections.identity', 'Identity')}
              </FieldLegend>
              <FieldSeparator />
              <FieldGroup className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label={t('contacts.fields.title', 'Title')} name="title" error={errors.title?.message}>
                  <Input
                    id="title"
                    {...register('title')}
                    placeholder={t('contacts.placeholders.title', 'Title')}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label={t('contacts.fields.name', 'Name')} name="name" error={errors.name?.message} required>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder={t('contacts.placeholders.name', "Person's full name")}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField
                  label={t('contacts.fields.jobTitle', 'Job Title')}
                  name="jobTitle"
                  error={errors.jobTitle?.message}
                  className="md:col-span-2">
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="jobTitle"
                      {...register('jobTitle')}
                      className="pl-10"
                      placeholder={t('contacts.placeholders.jobTitle', 'Job Title')}
                      disabled={isViewMode}
                    />
                  </div>
                </FormField>
              </FieldGroup>
            </FieldSet>

            {/* Location Section */}
            <FieldSet>
              <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
                <MapPin className="w-4 h-4" />
                {t('contacts.sections.location', 'Location')}
              </FieldLegend>
              <FieldSeparator />
              <FormField label={t('contacts.fields.address', 'Address')} name="address" error={errors.address?.message}>
                <Input
                  id="address"
                  {...register('address')}
                  placeholder={t('contacts.placeholders.address', 'Full physical Address')}
                  disabled={isViewMode}
                />
              </FormField>
            </FieldSet>

            {/* Communication Section */}
            <FieldSet>
              <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
                <Mail className="w-4 h-4" />
                {t('contacts.sections.communication', 'Communication')}
              </FieldLegend>
              <FieldSeparator />
              <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  label={t('contacts.fields.email1', 'Primary Email')}
                  name="email1"
                  error={errors.email1?.message}
                  required>
                  <Input
                    id="email1"
                    type="email"
                    {...register('email1')}
                    placeholder={t('contacts.placeholders.email', 'Email Address')}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label={t('contacts.fields.email2', 'Email 2')} name="email2" error={errors.email2?.message}>
                  <Input
                    id="email2"
                    type="email"
                    {...register('email2')}
                    placeholder={t('contacts.placeholders.email', 'Email Address')}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label={t('contacts.fields.email3', 'Email 3')} name="email3" error={errors.email3?.message}>
                  <Input
                    id="email3"
                    type="email"
                    {...register('email3')}
                    placeholder={t('contacts.placeholders.email', 'Email Address')}
                    disabled={isViewMode}
                  />
                </FormField>
              </FieldGroup>
            </FieldSet>

            {/* Telephony Section */}
            <FieldSet>
              <FieldLegend className="flex items-center gap-2 uppercase tracking-wider">
                <Phone className="w-4 h-4" />
                {t('contacts.sections.telephony', 'Telephony')}
              </FieldLegend>
              <FieldSeparator />
              <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  label={t('contacts.fields.phone1', 'Primary Phone')}
                  name="phone1"
                  error={errors.phone1?.message}
                  required>
                  <Input
                    id="phone1"
                    {...register('phone1')}
                    placeholder={t('contacts.placeholders.phonePrimary', 'Phone Number')}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label={t('contacts.fields.phone2', 'Phone 2')} name="phone2" error={errors.phone2?.message}>
                  <Input
                    id="phone2"
                    {...register('phone2')}
                    placeholder={t(
                      'contacts.placeholders.phoneGuidance',
                      "Phone Number (Prefixed with '+' and the country code)"
                    )}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label={t('contacts.fields.phone3', 'Phone 3')} name="phone3" error={errors.phone3?.message}>
                  <Input
                    id="phone3"
                    {...register('phone3')}
                    placeholder={t(
                      'contacts.placeholders.phoneGuidance',
                      "Phone Number (Prefixed with '+' and the country code)"
                    )}
                    disabled={isViewMode}
                  />
                </FormField>
              </FieldGroup>
            </FieldSet>
          </FieldGroup>
        </form>

        <DialogFooter className="p-4 border-t bg-slate-50 flex flex-row justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="h-10 px-6 border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors">
            {isViewMode ? t('common.close', 'Close') : t('common.closeDialog', 'Cancel')}
          </Button>
          <SubmitButton onClick={onSubmit} disabled={isSaving} isSaving={isSaving} isViewMode={isViewMode} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
